/**
 * Cosa conta come "risposta" a un documento di consenso.
 *
 * Condivisa fra portale (wizard L03) e gestionale (riacquisizione allo
 * sportello): la regola e' la stessa in entrambi i punti di raccolta, e
 * l'unico modo per garantirlo e' non riscriverla due volte. Questo file
 * non dipende da Firebase ne' da nulla di specifico di una delle due
 * entry, esattamente come `DocumentoLegaleView`.
 *
 * La CF registra `accepted: boolean` per ogni casella. Un `false` e' un
 * RIFIUTO, non un "non ho toccato". Per questo non si puo' inviare una
 * casella mai vista: sarebbe una dichiarazione falsa in un atto
 * probatorio.
 *
 * Un documento e' RISPOSTO solo in due casi:
 *  - tutte le sue caselle sono `true` (accettazione integrale);
 *  - tutte le sue caselle sono `false` (rifiuto esplicito del documento).
 *
 * Qualsiasi altra combinazione — mai toccato, o parzialmente spuntato —
 * e' `non_risposto`. L'invio e' bloccato. Il riepilogo non elenca questi
 * stati: non sono ne' accettazioni ne' rifiuti.
 *
 * Sui documenti a caselle (InBody, Genius, allegato) il `false` non si
 * ottiene togliendo la spunta: si ottiene solo dal rifiuto a livello di
 * documento. Togliere una spunta riporta quella casella a "non risposto".
 */

import type { DocumentoLegale } from './documento-legale.types';

export type StatoRispostaDocumento = 'non_risposto' | 'accettato' | 'rifiutato';

export const MESSAGGIO_DOCUMENTO_SENZA_RISPOSTA =
  'Indica se accetti o rifiuti questo documento.';

export function statoRispostaDocumento(
  doc: DocumentoLegale,
  voci: Record<string, boolean | undefined>
): StatoRispostaDocumento {
  if (doc.consensi.length === 0) return 'non_risposto';
  const valori = doc.consensi.map((c) => voci[c.id]);
  if (valori.every((v) => v === true)) return 'accettato';
  if (valori.every((v) => v === false)) return 'rifiutato';
  return 'non_risposto';
}

/** Documenti le cui caselle non sono una coppia si/no: serve il rifiuto di documento. */
export function usaRifiutoDiDocumento(doc: DocumentoLegale): boolean {
  return doc.consensi.some((c) => c.tipo !== 'binario');
}

export function idsDocumentiSenzaRisposta(
  documenti: readonly DocumentoLegale[],
  voci: Record<string, boolean | undefined>
): string[] {
  return documenti
    .filter((d) => statoRispostaDocumento(d, voci) === 'non_risposto')
    .map((d) => d.documentoId);
}
